import { Check, X } from "lucide-react";
import { useI18n } from "@/lib/i18n";

type Cell = { type: "check" } | { type: "x" } | { type: "text"; valueKey: string; noteKey?: string };

function CellView({ cell }: { cell: Cell }) {
  const { t } = useI18n();
  if (cell.type === "check")
    return (
      <div className="flex justify-center">
        <Check className="h-5 w-5 text-primary" strokeWidth={3} />
      </div>
    );
  if (cell.type === "x")
    return (
      <div className="flex justify-center">
        <X className="h-5 w-5 text-muted-foreground/60" strokeWidth={2.5} />
      </div>
    );
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-sm text-muted-foreground">{t(cell.valueKey)}</span>
      {cell.noteKey && (
        <span className="text-[11px] text-muted-foreground/60">{t(cell.noteKey)}</span>
      )}
    </div>
  );
}

const rows: { labelKey: string; cells: Cell[] }[] = [
  {
    labelKey: "comparison.row.cost",
    cells: [
      { type: "text", valueKey: "comparison.cell.adkavi.cost" },
      { type: "text", valueKey: "comparison.cell.agency.cost" },
      { type: "text", valueKey: "comparison.cell.diy.cost" },
      { type: "text", valueKey: "comparison.cell.tools.cost", noteKey: "comparison.cell.tools.cost.note" },
    ],
  },
  {
    labelKey: "comparison.row.launch",
    cells: [
      { type: "text", valueKey: "comparison.cell.adkavi.launch" },
      { type: "text", valueKey: "comparison.cell.agency.launch" },
      { type: "text", valueKey: "comparison.cell.diy.launch" },
      { type: "text", valueKey: "comparison.cell.tools.launch" },
    ],
  },
  { labelKey: "comparison.row.copyAi", cells: [{ type: "check" }, { type: "x" }, { type: "x" }, { type: "check" }] },
  { labelKey: "comparison.row.videoAi", cells: [{ type: "check" }, { type: "x" }, { type: "x" }, { type: "check" }] },
  { labelKey: "comparison.row.voice", cells: [{ type: "check" }, { type: "x" }, { type: "x" }, { type: "check" }] },
  { labelKey: "comparison.row.multiplatform", cells: [{ type: "check" }, { type: "x" }, { type: "x" }, { type: "x" }] },
  { labelKey: "comparison.row.optimize", cells: [{ type: "check" }, { type: "x" }, { type: "x" }, { type: "x" }] },
  { labelKey: "comparison.row.spy", cells: [{ type: "check" }, { type: "x" }, { type: "x" }, { type: "x" }] },
  { labelKey: "comparison.row.cancel", cells: [{ type: "check" }, { type: "x" }, { type: "check" }, { type: "check" }] },
  { labelKey: "comparison.row.creative", cells: [{ type: "check" }, { type: "check" }, { type: "x" }, { type: "check" }] },
  { labelKey: "comparison.row.carousel", cells: [{ type: "check" }, { type: "x" }, { type: "x" }, { type: "x" }] },
  { labelKey: "comparison.row.image", cells: [{ type: "check" }, { type: "x" }, { type: "x" }, { type: "check" }] },
];

export function Comparison() {
  const { t } = useI18n();
  const columns = ["AdKavi", t("comparison.col.agency"), t("comparison.col.diy"), t("comparison.col.tools")];

  return (
    <section className="py-20 px-6 relative">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
            {t("comparison.title")} <span className="text-gradient-orange">AdKavi</span>
          </h2>
        </div>

        <div className="rounded-2xl border border-border bg-card overflow-hidden pt-6">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse">
              <thead>
                <tr>
                  <th className="text-left p-4 md:p-5 text-sm font-medium text-muted-foreground"></th>
                  {columns.map((c, i) => {
                    const isAdKavi = i === 0;
                    return (
                      <th
                        key={c}
                        className={
                          "p-4 md:p-5 text-center align-bottom " +
                          (isAdKavi ? "bg-primary/5 border-x-2 border-t-2 border-primary rounded-t-xl relative" : "")
                        }
                      >
                        {isAdKavi && (
                          <div className="flex justify-center mb-2">
                            <span className="inline-block text-[11px] px-3 py-1 rounded-full bg-primary text-white whitespace-nowrap" style={{ fontWeight: 700, letterSpacing: "0.08em" }}>
                              {t("comparison.bestValue")}
                            </span>
                          </div>
                        )}
                        <div className={"text-sm md:text-base font-bold " + (isAdKavi ? "text-gradient-orange" : "text-foreground")}>
                          {c}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rIdx) => (
                  <tr key={row.labelKey} className="border-t border-border">
                    <td className="p-4 md:p-5 text-sm md:text-base font-medium text-foreground">
                      {t(row.labelKey)}
                    </td>
                    {row.cells.map((cell, i) => {
                      const isAdKavi = i === 0;
                      const isLast = rIdx === rows.length - 1;
                      return (
                        <td
                          key={i}
                          className={
                            "p-4 md:p-5 text-center " +
                            (isAdKavi
                              ? "bg-primary/5 border-x-2 border-primary " + (isLast ? "border-b-2 rounded-b-xl" : "")
                              : "")
                          }
                        >
                          <CellView cell={cell} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
